import React from 'react';
import clsx from 'clsx';
import { RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Button } from './Button';
import { EmptyState } from './EmptyState';

interface LoadErrorStateProps {
  /** Completes "Couldn’t load …", e.g. "your contacts". */
  what: string;
  onRetry: () => void;
  /** Reason to show under the title; defaults to a connection hint. */
  message?: string;
  /** No border/background — for use inside a card that already has one. */
  bare?: boolean;
  className?: string;
}

/** Shown in place of a list whose request failed, so it never sits on a skeleton or reads as empty. */
export const LoadErrorState: React.FC<LoadErrorStateProps> = ({ what, onRetry, message, bare, className }) => (
  <div
    role="alert"
    className={clsx(
      'flex flex-col items-center gap-3 px-6 py-14 text-center',
      !bare && 'rounded-2xl border border-border bg-surface shadow-soft',
      className,
    )}
  >
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger">
      <TriangleAlert size={22} />
    </span>
    <div>
      <p className="text-sm font-semibold text-text-primary">Couldn’t load {what}</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-text-muted">{message || 'Check your connection and try again.'}</p>
    </div>
    <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={onRetry}>
      Retry
    </Button>
  </div>
);

interface NoMatchesStateProps {
  onClear: () => void;
  description?: string;
  className?: string;
}

/** Empty result caused by a search or filter — never the first-run "create your first" copy. */
export const NoMatchesState: React.FC<NoMatchesStateProps> = ({ onClear, description, className }) => (
  <EmptyState
    icon={<SearchX size={28} />}
    title="Nothing matches your filters"
    description={description ?? 'Try a different search, or clear the filters to see everything.'}
    action={<Button variant="secondary" onClick={onClear}>Clear filters</Button>}
    className={className}
  />
);
